import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { MultiModelConfig, ModelCapability } from '../types/multimodal';
import { OpenRouterConfig } from './OpenRouterConfig';
import { fetchAvailableModels, getRecommendedModelsByCategory, ModelInfo } from '../services/modelService';

interface MultiModelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (configs: MultiModelConfig[]) => void;
  currentConfigs?: MultiModelConfig[];
  baseConfig?: OpenRouterConfig;
}

export const MultiModelSelector: React.FC<MultiModelSelectorProps> = ({
  isOpen,
  onClose,
  onSave,
  currentConfigs = [],
  baseConfig
}) => {
  const { t } = useTranslation();
  const { isPortuguese } = useLanguage();
  
  const [models, setModels] = useState<MultiModelConfig[]>([]);
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<'vision' | 'text' | 'code' | 'creative' | 'reasoning'>('vision');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Initialize with current configs or load default models from API
  useEffect(() => {
    if (currentConfigs.length > 0) {
      setModels(currentConfigs);
    } else {
      loadDefaultModels();
    }
  }, [currentConfigs, baseConfig]);

  // Load default models from API
  const loadDefaultModels = async () => {
    try {
      const recommendedModels = await getRecommendedModelsByCategory('vision');
      const defaultModels: MultiModelConfig[] = recommendedModels.slice(0, 3).map((modelInfo, index) => ({
        id: `model-${Date.now()}-${index}`,
        name: modelInfo.name,
        config: {
          apiKey: baseConfig?.apiKey || '',
          model: modelInfo.id,
          temperature: 0.7,
          maxTokens: 2000
        },
        enabled: true,
        order: index,
        capabilities: ['text', 'image']
      }));
      setModels(defaultModels);
    } catch (error) {
      console.error('Error loading default models:', error);
      // Fallback to basic model if API fails
      const fallbackModel: MultiModelConfig = {
        id: `model-${Date.now()}`,
        name: 'Claude 3.5 Sonnet',
        config: {
          apiKey: baseConfig?.apiKey || '',
          model: 'anthropic/claude-3.5-sonnet',
          temperature: 0.7,
          maxTokens: 2000
        },
        enabled: true,
        order: 0,
        capabilities: ['text']
      };
      setModels([fallbackModel]);
    }
  };

  // Load available models
  useEffect(() => {
    if (isOpen && availableModels.length === 0) {
      loadAvailableModels();
    }
  }, [isOpen]);

  const loadAvailableModels = async () => {
    setIsLoadingModels(true);
    try {
      const models = await fetchAvailableModels();
      setAvailableModels(models);
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setIsLoadingModels(false);
    }
  };

  const addModel = () => {
    if (models.length >= 5) return; // Máximo de 5 modelos
    
    const newModel: MultiModelConfig = {
      id: `model-${Date.now()}`,
      name: t('multiModel.newModel', 'New Model'),
      config: {
        apiKey: baseConfig?.apiKey || '',
        model: 'anthropic/claude-3.5-sonnet',
        temperature: 0.7,
        maxTokens: 2000
      },
      enabled: true,
      order: models.length,
      capabilities: ['text']
    };
    
    setModels([...models, newModel]);
  };

  const removeModel = (id: string) => {
    if (models.length <= 1) return; // Mínimo de 1 modelo
    setModels(models.filter(m => m.id !== id));
  };

  const updateModel = (id: string, updates: Partial<MultiModelConfig>) => {
    setModels(models.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const applyPreset = async (preset: 'vision' | 'text' | 'code' | 'creative' | 'reasoning') => {
    try {
      const recommendedModels = await getRecommendedModelsByCategory(preset);
      const presetModels: MultiModelConfig[] = recommendedModels.slice(0, 3).map((modelInfo, index) => ({
        id: `model-${Date.now()}-${index}`,
        name: modelInfo.name,
        config: {
          apiKey: baseConfig?.apiKey || '',
          model: modelInfo.id,
          temperature: preset === 'creative' ? 0.9 : preset === 'reasoning' ? 0.3 : 0.7,
          maxTokens: preset === 'reasoning' ? 4000 : 2000
        },
        enabled: true,
        order: index,
        capabilities: getCapabilitiesForPreset(preset)
      }));
      setModels(presetModels);
      setSelectedPreset(preset);
    } catch (error) {
      console.error('Error loading preset models:', error);
    }
  };

  const getCapabilitiesForPreset = (preset: 'vision' | 'text' | 'code' | 'creative' | 'reasoning'): ModelCapability[] => {
    switch (preset) {
      case 'vision': return ['text', 'image'];
      case 'code': return ['text', 'code'];
      case 'creative': return ['text', 'creative'];
      case 'reasoning': return ['text', 'reasoning'];
      default: return ['text'];
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newModels = [...models];
    const draggedModel = newModels[draggedIndex];
    newModels.splice(draggedIndex, 1);
    newModels.splice(dropIndex, 0, draggedModel);
    
    // Update order
    newModels.forEach((model, index) => {
      model.order = index;
    });
    
    setModels(newModels);
    setDraggedIndex(null);
  };

  const handleSave = () => {
    const validModels = models.filter(m => m.config.apiKey && m.config.model);
    onSave(validModels);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                {t('multiModel.title', '🎯 Multi-Model Configuration')}
              </h2>
              <p className="text-purple-100 mt-1">
                {t('multiModel.subtitle', 'Configure multiple AI models for enhanced responses')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Presets */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              {t('multiModel.presets.title', '⚡ Quick Presets')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(['vision', 'text', 'code', 'creative', 'reasoning'] as const).map((preset) => (
                <button
                  key={preset}
                  onClick={() => applyPreset(preset)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                    selectedPreset === preset
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
                  }`}
                >
                  <div className="text-sm font-medium capitalize">
                    {t(`multiModel.presets.${preset}`, preset)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Models Configuration */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800">
                {t('multiModel.models.title', '🤖 Selected Models')} ({models.length}/5)
              </h3>
              <button
                onClick={addModel}
                disabled={models.length >= 5}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {t('multiModel.addModel', '+ Add Model')}
              </button>
            </div>

            <div className="space-y-4">
              {models.map((model, index) => (
                <div
                  key={model.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className="bg-gray-50 rounded-lg p-4 border-2 border-transparent hover:border-purple-200 transition-all duration-200 cursor-move"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-medium">
                        {index + 1}
                      </div>
                      <input
                        type="text"
                        value={model.name}
                        onChange={(e) => updateModel(model.id, { name: e.target.value })}
                        className="text-lg font-medium bg-transparent border-none outline-none focus:bg-white focus:px-2 focus:py-1 rounded"
                        placeholder="Model Name"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={model.enabled}
                          onChange={(e) => updateModel(model.id, { enabled: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-600">
                          {t('multiModel.enabled', 'Enabled')}
                        </span>
                      </label>
                      {models.length > 1 && (
                        <button
                          onClick={() => removeModel(model.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Model Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('multiModel.model', 'Model')}
                      </label>
                      <select
                        value={model.config.model}
                        onChange={(e) => updateModel(model.id, { 
                          config: { ...model.config, model: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        {isLoadingModels ? (
                          <option>{t('multiModel.loading', 'Loading...')}</option>
                        ) : (
                          availableModels.map((availableModel) => (
                            <option key={availableModel.id} value={availableModel.id}>
                              {availableModel.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    {/* Temperature */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('multiModel.temperature', 'Temperature')}: {model.config.temperature}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={model.config.temperature}
                        onChange={(e) => updateModel(model.id, { 
                          config: { ...model.config, temperature: parseFloat(e.target.value) }
                        })}
                        className="w-full"
                      />
                    </div>

                    {/* Max Tokens */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('multiModel.maxTokens', 'Max Tokens')}
                      </label>
                      <input
                        type="number"
                        min="100"
                        max="8000"
                        value={model.config.maxTokens}
                        onChange={(e) => updateModel(model.id, { 
                          config: { ...model.config, maxTokens: parseInt(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            {t('multiModel.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
          >
            {t('multiModel.save', 'Save Configuration')}
          </button>
        </div>
      </div>
    </div>
  );
};